"use client";

import React from "react";
import { Users, Briefcase, ShieldCheck, ArrowRight, Wifi, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const vehicles = [{
  id: "luxury-sedan",
  title: "Luxury Sedan",
  classCategory: "Executive Sedan",
  tagline: "Private executive travel & airport transfers",
  description: "Whisper-quiet, hand-detailed travel tailored for corporate executives, VIP airport itineraries, and seamless city transit across Illinois.",
  passengers: "Up to 3 passengers",
  luggage: "3 standard luggage items",
  amenities: ["Complimentary Wi-Fi", "Real-Time Flight Tracking", "Rear Climate Control"],
  badge: "Executive Class",
  startingRate: "Guaranteed Flat Rate",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_sedan_fleet.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_sedan_fleet.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/luxury_sedan_fleet.webp-wvc-sizes",
    alt: "Pristine gloss black branded executive luxury sedan with bespoke chauffeur service"
  }
}, {
  id: "executive-suv",
  title: "Executive SUV",
  classCategory: "First Class SUV",
  tagline: "Spacious group comfort & extended itineraries",
  description: "Expansive cabin legroom and ample cargo space designed for board delegations, private family journeys, and regional interstate routes.",
  passengers: "Up to 6 passengers",
  luggage: "6 standard luggage items",
  amenities: ["Extended Cargo Room", "Dual Power Outlets", "Premium Acoustic Glass"],
  badge: "Group First Class",
  startingRate: "Guaranteed Flat Rate",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_suv_fleet.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_suv_fleet.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_suv_fleet.png-wvc-sizes",
    alt: "Spacious gloss black branded luxury executive SUV tailored for group travel"
  }
}];
export default function Fleet() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.09
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 24
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-fleet" data-nav="dark" className="dark bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {/* Subtle radial ambient background glow */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[480px] bg-primary/5 rounded-lg blur-3xl" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-5 border border-border">
            <ShieldCheck className="size-4 text-primary" />
            <span>Branded Luxury Fleet</span>
          </div>

          <h2 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Our executive <span className="text-primary italic font-serif">fleet</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Travel in impeccably maintained, signature SANTA LIMO vehicles. Every reservation includes guaranteed flat pricing, verified chauffeurs, and effortless comfort.
          </p>
        </div>

        {/* 2-Card Vehicle Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {vehicles.map((car, i) => <motion.div key={car.id} variants={cardVariants} data-index={i} className="group relative flex flex-col rounded-xl bg-card border border-border overflow-hidden transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-2xl hover:shadow-black/70">
              {/* 1px Lounge Gold Hairline on top border */}
              <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-primary/70 via-primary/30 to-transparent" aria-hidden="true" />

              {/* Vehicle Image Showcase */}
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                <img src={car.image.src} data-wvc-srcset={car.image.srcset} data-wvc-sizes={car.image.sizes} alt={car.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" aria-hidden="true" />

                {/* Top Badges */}
                <div className="absolute top-4 inset-x-4 flex items-center justify-between">
                  <Badge variant="outline" className="bg-card/90 backdrop-blur-sm text-foreground border-border text-xs px-3 py-1 font-medium rounded-lg">
                    {car.badge}
                  </Badge>
                  <span className="text-xs font-mono font-medium px-2.5 py-1 rounded-lg bg-primary/15 text-primary border border-primary/30 backdrop-blur-sm">
                    {car.startingRate}
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 sm:p-8 flex flex-col flex-1 justify-between">
                <div>
                  <div className="flex flex-col gap-1 mb-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {car.classCategory}
                    </span>
                    <h3 className="font-default text-2xl sm:text-3xl font-bold text-card-foreground tracking-tight">
                      {car.title}
                    </h3>
                  </div>

                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 font-sans">
                    {car.description}
                  </p>

                  {/* Core Capacity Specs */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 p-3.5 rounded-lg bg-secondary/60 border border-border">
                    <div className="flex items-center gap-3 text-sm text-card-foreground">
                      <div className="flex size-8 items-center justify-center rounded-lg bg-card text-primary shrink-0 border border-border/80">
                        <Users className="size-4" />
                      </div>
                      <span className="font-sans font-medium">{car.passengers}</span>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-card-foreground">
                      <div className="flex size-8 items-center justify-center rounded-lg bg-card text-primary shrink-0 border border-border/80">
                        <Briefcase className="size-4" />
                      </div>
                      <span className="font-sans font-medium">{car.luggage}</span>
                    </div>
                  </div>

                  {/* Included Amenities List */}
                  <div className="space-y-2 mb-8 border-t border-border/70 pt-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Vehicle Inclusions
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                      {car.amenities.map((amenity, idx) => <li key={idx} className="flex items-center gap-1.5 text-xs text-muted-foreground font-sans">
                          <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                          <span>{amenity}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>

                {/* Card Action */}
                <div className="pt-2">
                  <Button asChild variant="outline" className="w-full h-12 rounded-lg border-border/90 bg-card hover:bg-secondary text-foreground hover:text-foreground font-semibold justify-between px-5 transition-[background-color,border-color,box-shadow] duration-200 group-hover:border-primary/60 group-hover:shadow-md">
                    <Link to="/OrderPage">
                      <span className="text-sm font-semibold">Reserve {car.title}</span>
                      <div className="flex items-center gap-1.5 text-primary text-xs font-semibold">
                        <span>View Rates</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </div>
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Reassurance Footer Note */}
        <div className="mt-16 text-center border-t border-border/60 pt-8 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 mb-2">
            <Sparkles className="size-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Signature Standards
            </span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed font-sans">
            All vehicles are proudly branded, immaculate black livery operated by vetted professional chauffeurs. Flat one-way rates with zero surge pricing guaranteed.
          </p>
        </div>
      </div>
    </section>;
}